import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Redirect } from 'wouter';
import { useWouterRouter } from '../hooks/useWouterNavigation';
import { Toaster, toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizer } from '../contexts/OrganizerContext';
import { OrganizerBottomNav } from '../pages/DesignSystem/patterns/BottomNav/OrganizerBottomNav';
import { Icon } from '../pages/DesignSystem';
import { OnboardingModal } from '../components/organizer/OnboardingModal';
import { MobileCarousel } from '../components/organizer/ProductTour';
import { useOnboarding } from '../hooks/useOnboarding';
import { useProductTour } from '../hooks/useProductTour';
import './styles/organizer-layout.css';

// Lazy load ImportModal (includes SheetJS ~400KB)
const ImportModal = lazy(() => import('../components/organizer/EventImport/ImportModal'));

interface OrganizerLayoutProps {
  children: React.ReactNode;
}

// 🔧 FIX: Tracking pour history.back() au lieu de push quand on revient au dashboard
let _orgMobileOnSubPage = false;

const OrganizerLayout: React.FC<OrganizerLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const { organizerProfile, updateProfile } = useOrganizer();
  const { navigate, location } = useWouterRouter();

  // Onboarding state (PRD-onboarding-organisateur.md)
  const {
    needsOnboarding,
    currentStep,
    nextStep,
    prevStep,
    completeOnboarding,
    error: onboardingError,
  } = useOnboarding(organizerProfile, user?.uid || null);

  // 🔧 FIX (item 2 - PRD robustesse réseau faible): l'état d'erreur d'onboarding
  // était calculé par le hook mais jamais récupéré/affiché ici. On le surface
  // désormais en toast pour que l'utilisateur voie toujours quand ça échoue.
  useEffect(() => {
    if (onboardingError) {
      toast.error(onboardingError);
    }
  }, [onboardingError]);

  // Product Tour state (PRD-product-tour-organisateur.md)
  const {
    isRunning: isTourRunning,
    completeTour,
    skipTour,
    replayTour,
  } = useProductTour();

  // Listen for product tour start event (from Help menu)
  useEffect(() => {
    const handleStartTour = () => {
      replayTour();
    };
    window.addEventListener('startProductTour', handleStartTour);
    return () => window.removeEventListener('startProductTour', handleStartTour);
  }, [replayTour]);

  // Déterminer l'onglet actif en fonction de l'URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/dashboard') || path === '/organizer' || path === '/organizer/') {
      return 'dashboard';
    } else if (path.includes('/events')) {
      return 'events';
    } else if (path.includes('/profile')) {
      return 'profile';
    }
    return 'dashboard';
  };

  const [activeTab, setActiveTab] = useState<'dashboard' | 'events' | 'profile'>(getActiveTab());

  // Import feature state (PRD-import-intelligent-events.md)
  const [isAddEventSheetOpen, setIsAddEventSheetOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Mettre à jour l'onglet actif quand l'URL change
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);

  // 🔧 FIX: Navigation propre entre tabs organizer
  // Dashboard → tab : push (1 entrée). Tab → tab : replace (back va au dashboard).
  // Tab → dashboard (nav) : history.back() au lieu de push.
  const handleTabChange = useCallback((tab: 'dashboard' | 'events' | 'profile') => {
    const currentPath = location.pathname;
    const isOnDashboard = currentPath.includes('/dashboard') || currentPath === '/organizer' || currentPath === '/organizer/';

    setActiveTab(tab);

    if (tab === 'dashboard') {
      if (_orgMobileOnSubPage) {
        _orgMobileOnSubPage = false;
        window.history.back();
      } else {
        navigate('/organizer/dashboard');
      }
    } else {
      const targetPath = tab === 'events' ? '/organizer/events' : '/organizer/profile';

      if (isOnDashboard) {
        _orgMobileOnSubPage = true;
        navigate(targetPath);
      } else {
        navigate(targetPath, { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // FAB menu: "+" button opens the expanded menu (PRD-import-intelligent-events.md)
  const handlePlusButtonClick = useCallback(() => {
    setIsAddEventSheetOpen(true);
    setIsMenuClosing(false);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setIsAddEventSheetOpen(false);
    setIsMenuClosing(true);
    // Wait for items to fade out before allowing main FAB to take flow space
    setTimeout(() => setIsMenuClosing(false), 200);
  }, []);

  const handleCreateEvent = useCallback(() => {
    setIsAddEventSheetOpen(false);
    navigate('/organizer/events');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openCreateEvent'));
    }, 100);
  }, [navigate]);

  const handleOpenImport = useCallback(() => {
    setIsAddEventSheetOpen(false);
    setIsImportModalOpen(true);
  }, []);

  const handleCloseImport = useCallback(() => {
    setIsImportModalOpen(false);
  }, []);

  const handleGoToDrafts = useCallback(() => {
    setIsImportModalOpen(false);
    navigate('/organizer/events');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('switchToDrafts'));
    }, 100);
  }, [navigate]);

  // Rediriger vers login si non authentifié
  if (!user) {
    return <Redirect to="/login" />;
  }

  return (
    <div className="organizer-layout">
      <main className="organizer-layout__content">
        {children}
      </main>

      {/* FAB Menu: Create Event + Import (PRD-import-intelligent-events.md) */}
      {(activeTab === 'dashboard' || activeTab === 'events') &&
       !location.pathname.includes('/subscription') && (
        <>
          {/* Backdrop overlay when menu is open */}
          {isAddEventSheetOpen && (
            <div
              className="fab-menu__backdrop"
              onClick={handleCloseMenu}
              aria-hidden="true"
            />
          )}

          <div className={`fab-menu ${isAddEventSheetOpen ? 'fab-menu--open' : ''} ${isMenuClosing ? 'fab-menu--closing' : ''}`}>
            {/* Import option */}
            <button
              className="fab-menu__item fab-menu__item--import"
              onClick={handleOpenImport}
              tabIndex={isAddEventSheetOpen ? 0 : -1}
            >
              <Icon name="Upload" size={18} />
              <div className="fab-menu__item-text">
                <span className="fab-menu__item-label">Import events</span>
                <span className="fab-menu__item-hint">Instagram, photo, PDF, Excel...</span>
              </div>
            </button>

            {/* Create option */}
            <button
              className="fab-menu__item fab-menu__item--create"
              onClick={handleCreateEvent}
              tabIndex={isAddEventSheetOpen ? 0 : -1}
            >
              <Icon name="PenLine" size={18} />
              <div className="fab-menu__item-text">
                <span className="fab-menu__item-label">Create manually</span>
                <span className="fab-menu__item-hint">Quickly fill in the details</span>
              </div>
            </button>

            {/* Main FAB — only visible when menu is closed */}
            <button
              className={`fab-menu__main ${(isAddEventSheetOpen || isMenuClosing) ? 'fab-menu__main--hidden' : ''}`}
              onClick={handlePlusButtonClick}
              aria-hidden={isAddEventSheetOpen || isMenuClosing}
            >
              <Icon name="Plus" size={20} />
              <span>Create Event</span>
            </button>
          </div>
        </>
      )}

      {/* Import Modal - lazy loaded (PRD-import-intelligent-events.md) */}
      {isImportModalOpen && (
        <Suspense fallback={null}>
          <ImportModal
            isOpen={isImportModalOpen}
            onClose={handleCloseImport}
            onGoToDrafts={handleGoToDrafts}
          />
        </Suspense>
      )}

      <OrganizerBottomNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        className="organizer-layout__bottom-nav"
        onProfileClick={() => navigate('/organizer/profile')}
      />

      {/* Onboarding Modal - PRD-onboarding-organisateur.md */}
      <OnboardingModal
        isOpen={needsOnboarding}
        organizerProfile={organizerProfile}
        userId={user?.uid || ''}
        currentStep={currentStep}
        onNextStep={nextStep}
        onPrevStep={prevStep}
        onComplete={completeOnboarding}
        onProfileUpdate={updateProfile}
      />

      {/* Product Tour - PRD-product-tour-organisateur.md */}
      <MobileCarousel
        isOpen={isTourRunning && !needsOnboarding}
        onComplete={completeTour}
        onSkip={skipTour}
      />

      <Toaster
        position="top-right"
        toastOptions={{
          className: 'organizer-toast',
          duration: 3000
        }}
      />
    </div>
  );
};

export default OrganizerLayout; 